import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services/api";
import type { DocMeta, Fund } from "../model/types";

const STORAGE_FUND_ID = "s2_selectedFundId";

function readStoredFundId(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_FUND_ID) ?? null;
  } catch {
    return null;
  }
}

interface S2DashboardContextValue {
  funds: Fund[];
  docMeta: Record<string, DocMeta>;
  isLoading: boolean;
  error: Error | null;
  selectedFundId: string | null;
  setSelectedFundId: (id: string | null) => void;
  selectedFundIds: string[];
  setSelectedFundIds: (ids: string[]) => void;
  caSelectedPeriod: string | null;
  setCASelectedPeriod: (period: string | null) => void;
  caSelectedDocId: string | null;
  setCASelectedDocId: (docId: string | null) => void;
  foSelectedDocId: string | null;
  setFOSelectedDocId: (docId: string | null) => void;
  portSelectedDocId: string | null;
  setPortSelectedDocId: (docId: string | null) => void;
  auditDocId: string | null;
  setAuditDocId: (docId: string | null) => void;
  selectedFund: Fund | null;
  getDocMeta: (docId: string) => DocMeta | null;
}

const S2DashboardContext = createContext<S2DashboardContextValue | null>(null);

export function S2DashboardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedFundId, setSelectedFundIdState] = useState<string | null>(readStoredFundId);
  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);

  const setSelectedFundId = useCallback((id: string | null) => {
    setSelectedFundIdState(id);
    try {
      if (id) sessionStorage.setItem(STORAGE_FUND_ID, id);
      else sessionStorage.removeItem(STORAGE_FUND_ID);
    } catch {
      // Ignore storage failures — UI state remains valid in memory.
    }
  }, []);
  const [caSelectedPeriod, setCASelectedPeriod] = useState<string | null>(null);
  const [caSelectedDocId, setCASelectedDocId] = useState<string | null>(null);
  const [foSelectedDocId, setFOSelectedDocId] = useState<string | null>(null);
  const [portSelectedDocId, setPortSelectedDocId] = useState<string | null>(
    null,
  );
  const [auditDocId, setAuditDocId] = useState<string | null>(null);

  const {
    data: rawFunds = [],
    isLoading: fundsLoading,
    error: fundsError,
  } = useQuery({
    queryKey: ["s2", "funds"],
    queryFn: api.getFunds,
    staleTime: 60_000,
  });

  const {
    data: docMeta = {},
    isLoading: metaLoading,
    error: metaError,
  } = useQuery({
    queryKey: ["s2", "docMeta"],
    queryFn: api.getDocMeta,
    staleTime: 60_000,
  });

  const funds = useMemo<Fund[]>(() => {
    const byFundId = new Map<string, Fund>();
    for (const fund of rawFunds) {
      const key = fund.fund_id;
      const existing = byFundId.get(key);
      if (!existing) {
        byFundId.set(key, {
          ...fund,
          doc_ids: Array.from(new Set((fund.doc_ids ?? []).filter(Boolean))),
          doc_meta: { ...(fund.doc_meta ?? {}) },
        });
        continue;
      }
      byFundId.set(key, {
        ...existing,
        fund_name: existing.fund_name || fund.fund_name || key,
        doc_ids: Array.from(
          new Set([
            ...(existing.doc_ids ?? []).filter(Boolean),
            ...(fund.doc_ids ?? []).filter(Boolean),
          ]),
        ),
        doc_meta: {
          ...(existing.doc_meta ?? {}),
          ...(fund.doc_meta ?? {}),
        },
      });
    }
    return Array.from(byFundId.values());
  }, [rawFunds]);

  const selectedFund =
    funds.find((fund) => fund.fund_id === selectedFundId) ?? null;

  const getDocMeta = (docId: string): DocMeta | null => docMeta[docId] ?? null;

  return (
    <S2DashboardContext.Provider
      value={{
        funds,
        docMeta,
        isLoading: fundsLoading || metaLoading,
        error: (fundsError ?? metaError) as Error | null,
        selectedFundId,
        setSelectedFundId,
        selectedFundIds,
        setSelectedFundIds,
        caSelectedPeriod,
        setCASelectedPeriod,
        caSelectedDocId,
        setCASelectedDocId,
        foSelectedDocId,
        setFOSelectedDocId,
        portSelectedDocId,
        setPortSelectedDocId,
        auditDocId,
        setAuditDocId,
        selectedFund,
        getDocMeta,
      }}
    >
      {children}
    </S2DashboardContext.Provider>
  );
}

export function useS2DashboardContext(): S2DashboardContextValue {
  const context = useContext(S2DashboardContext);
  if (!context) {
    throw new Error(
      "useS2DashboardContext must be used inside S2DashboardProvider",
    );
  }
  return context;
}
